var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../../layout/components/loaders');
var modal = require('../../layout/components/modal');
var treeLocations = require('../../components/treeLocations');

function normalizeRid(value) {
  if (value == null) return null;
  var rid = typeof value === 'object' && value['@rid'] ? value['@rid'] : value;
  rid = String(rid || '');
  if (!rid) return null;
  return rid.charAt(0) === '#' ? rid : '#' + rid;
}

function formatLocationSegment(node) {
  if (!node || typeof node !== 'object') return '';
  if (node.name === 'root' || node.name === 'Home') return 'Home';
  if (node['@class'] === 'Room' && node.name && node.roomNumber) {
    return node.name + ' ' + node.roomNumber;
  }
  return node.name || node.roomNumber || node['@class'] || '';
}

function normalizeLocationPath(path) {
  var nodes = Array.isArray(path) ? path.slice() : [];
  if (nodes.length <= 1) return nodes;

  var first = nodes[0] || {};
  var last = nodes[nodes.length - 1] || {};
  var firstLooksRoot = first.name === 'root' || first.name === 'Home' || first['@class'] === 'Dashboard';
  var lastLooksRoot = last.name === 'root' || last.name === 'Home' || last['@class'] === 'Dashboard';
  if (!firstLooksRoot && lastLooksRoot) nodes.reverse();
  return nodes;
}

function buildLocationPathLabel(locationRecord) {
  var nodes = normalizeLocationPath(locationRecord && locationRecord.path);
  if (!nodes.length && locationRecord) nodes = [locationRecord];
  return nodes.map(formatLocationSegment).filter(Boolean).join(' / ');
}

function loadLocationSummary(ctrl, rid) {
  var normalizedRid = normalizeRid(rid);
  var requestId = ++ctrl._locationRequest;
  ctrl.locationRid = normalizedRid;
  ctrl.locationRecord = normalizedRid ? { '@rid': normalizedRid } : {};
  ctrl.locationPathLabel = '';
  ctrl.locationLoading = Boolean(normalizedRid);

  if (!normalizedRid) {
    m.redraw();
    return Promise.resolve(null);
  }

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/site/' + normalizedRid.slice(1),
    method: 'GET',
    background: true
  })
  .then(function (locationRecord) {
    if (ctrl._locationRequest === requestId && ctrl.locationRid === normalizedRid) {
      ctrl.locationRecord = locationRecord || { '@rid': normalizedRid };
      ctrl.locationPathLabel = buildLocationPathLabel(locationRecord) || normalizedRid;
      ctrl.locationLoading = false;
      m.redraw();
    }
    return locationRecord;
  })
  .catch(function () {
    if (ctrl._locationRequest === requestId && ctrl.locationRid === normalizedRid) {
      ctrl.locationPathLabel = normalizedRid;
      ctrl.locationLoading = false;
      m.redraw();
    }
    return null;
  });
}

function syncLocation(ctrl, record) {
  var nextRid = normalizeRid(record && record.locationRid);
  if (nextRid !== ctrl.locationRid) return loadLocationSummary(ctrl, nextRid);
  return Promise.resolve(ctrl.locationRecord);
}

function openLocationPicker(ctrl, record) {
  ctrl.saveError = '';
  var currentSelection = ctrl.locationRecord && ctrl.locationRecord['@rid']
    ? ctrl.locationRecord
    : (ctrl.locationRid ? { '@rid': ctrl.locationRid } : {});
  var modalSelection = prop(currentSelection);

  function submit() {
    var nextSelection = modalSelection() || {};
    var nextRid = normalizeRid(nextSelection['@rid']);
    if (!nextRid) {
      ctrl.saveError = 'Choose a location before saving.';
      m.redraw();
      return Promise.reject(new Error(ctrl.saveError));
    }
    if (nextRid === ctrl.locationRid) {
      modal.vm.close();
      return Promise.resolve();
    }

    ctrl.locationSaving = true;
    ctrl.saveError = '';
    return loaders.requestWithLoader({
      loader: 'submit',
      url: '/api/v1/report/' + String(record['@rid']).replace(/^#/, ''),
      method: 'PUT',
      body: { locationRid: nextRid }
    })
    .then(function (updatedReport) {
      record.locationRid = nextRid;
      return loadLocationSummary(ctrl, nextRid).then(function () {
        if (typeof ctrl.onSaved === 'function') ctrl.onSaved(updatedReport, record);
        modal.vm.close();
        return updatedReport;
      });
    })
    .catch(function (error) {
      ctrl.saveError = error && error.error
        ? error.error
        : (error && error.message ? error.message : 'Could not save the report location.');
      return Promise.reject(error);
    })
    .finally(function () {
      ctrl.locationSaving = false;
      m.redraw();
    });
  }

  modal.vm.title('Choose Location');
  modal.vm.body = function () {
    return m('form', { role: 'form' }, [
      m('div.f5.mid-gray.mb3', 'Pick the location this report should use.'),
      ctrl.saveError ? m('div.red.mb3', ctrl.saveError) : null,
      m(treeLocations, { selector: modalSelection })
    ]);
  };
  modal.vm.buttons([
    { label: 'Cancel', class: 'dib mr2', func: modal.vm.close },
    { label: 'Save', class: 'btn-good dib', func: submit }
  ]);
  modal.vm.open();
}

var component = {};

component.oninit = function (vnode) {
  var ctrl = this;
  ctrl.locationRid = null;
  ctrl.locationRecord = {};
  ctrl.locationPathLabel = '';
  ctrl.locationLoading = false;
  ctrl.locationSaving = false;
  ctrl.saveError = '';
  ctrl.onSaved = vnode.attrs && vnode.attrs.onSaved;
  ctrl._locationRequest = 0;
  syncLocation(ctrl, vnode.attrs && vnode.attrs.record);
};

component.view = function (vnode) {
  var ctrl = this;
  var record = vnode.attrs && vnode.attrs.record;
  var sectionClass = vnode.attrs && Object.prototype.hasOwnProperty.call(vnode.attrs, 'class')
    ? vnode.attrs.class
    : 'mh3 mt2 mb4';
  ctrl.onSaved = vnode.attrs && vnode.attrs.onSaved;
  syncLocation(ctrl, record);

  var label = ctrl.locationPathLabel
    || (ctrl.locationLoading ? 'Loading location…' : '')
    || (ctrl.locationRid || 'Choose a location');
  var hasLocation = Boolean(ctrl.locationRid);

  return m('section', { class: sectionClass }, [
    m('div.f5.fw6.mb2', 'Location'),
    m('div.ba.b--moon-gray.br2.ph3.pv3', [
      m('button.w-100.tl.pointer.pa0.bn.bg-transparent', {
        type: 'button',
        disabled: ctrl.locationSaving,
        style: { appearance: 'none' },
        onclick: function () { openLocationPicker(ctrl, record); }
      }, [
        m('span.f3.lh-title' + (hasLocation ? '.blue' : '.moon-gray'), label)
      ])
    ]),
    ctrl.saveError ? m('div.red.f6.mt2', ctrl.saveError) : null
  ]);
};

component.__testables = {
  normalizeRid: normalizeRid,
  buildLocationPathLabel: buildLocationPathLabel,
  loadLocationSummary: loadLocationSummary,
  syncLocation: syncLocation
};

module.exports = component;
